/**
 * Canvas-side of the image editor. Pure geometry helpers first, then the one
 * function that actually touches a canvas.
 *
 * ## The coordinate system (the thing that is easy to get wrong)
 *
 * `react-easy-crop` reports `croppedAreaPixels` in the **rotated bounding box**
 * of the source image, in natural pixels — not in the source image's own frame.
 * (See `computeCroppedArea` in the library: it derives the rect from
 * `rotateSize(naturalWidth, naturalHeight, rotation)`.)
 *
 * So the export is two steps, in this order, and no other:
 *   1. draw the source into a canvas the size of that rotated bounding box,
 *      applying flip then rotation about the centre;
 *   2. copy `croppedAreaPixels` out of *that* canvas.
 *
 * Flipping is applied inside the same pass rather than by pre-flipping the
 * source, because the flip is an inner transform (`scaleX(-1)` applied to the
 * media before the rotate/scale chain): it changes what is inside the bounding
 * box but not its geometry, so the reported rect stays valid.
 */

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EditTransform {
  /** Degrees, clockwise. Any value; multiples of 90 are the common case. */
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
}

export interface Size {
  width: number;
  height: number;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;

/** Ratio choices offered in the editor. `null` means "the source's own ratio". */
export const ASPECT_PRESETS: { label: string; value: number | null }[] = [
  { label: 'Original', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:4', value: 3 / 4 },
  { label: '2:3', value: 2 / 3 },
  { label: '9:16', value: 9 / 16 },
];

/** Size of `width × height` after rotating by `rotation` degrees. */
export function rotateSize(width: number, height: number, rotation: number): Size {
  const rad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Keep rotation in [0, 360) so the slider and the ±90° buttons agree. */
export function normalizeRotation(rotation: number): number {
  const wrapped = rotation % 360;
  // `+ 0` turns the `-0` that `-360 % 360` produces back into `0`, so a full
  // reverse turn compares equal to "no rotation" in `isIdentityEdit`.
  return wrapped < 0 ? wrapped + 360 : wrapped + 0;
}

/**
 * Clip a crop rect to the canvas it will be read from.
 *
 * `croppedAreaPixels` is rounded and can overhang the bounding box by a pixel;
 * `drawImage` silently pads the overhang with transparent black, which shows up
 * as a hairline of garbage down one edge of the exported image.
 */
export function clampArea(area: CropArea, bounds: Size): CropArea {
  const width = Math.max(1, Math.min(Math.round(area.width), Math.round(bounds.width)));
  const height = Math.max(1, Math.min(Math.round(area.height), Math.round(bounds.height)));
  const x = clamp(Math.round(area.x), 0, Math.max(0, Math.round(bounds.width) - width));
  const y = clamp(Math.round(area.y), 0, Math.max(0, Math.round(bounds.height) - height));
  return { x, y, width, height };
}

/**
 * Scale an output size down so it fits `maxWidth` (and, if given, the server's
 * `max_pixels` ceiling). Never scales up — enlarging a crop adds no detail and
 * would silently inflate the stored file.
 */
export function fitOutputSize(size: Size, maxWidth?: number | null, maxPixels?: number | null): Size {
  let { width, height } = size;
  if (maxWidth && width > maxWidth) {
    height = Math.max(1, Math.round((height * maxWidth) / width));
    width = maxWidth;
  }
  if (maxPixels && width * height > maxPixels) {
    const factor = Math.sqrt(maxPixels / (width * height));
    width = Math.max(1, Math.floor(width * factor));
    height = Math.max(1, Math.floor(height * factor));
  }
  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
}

export interface OutputFormat {
  mime: string;
  extension: string;
  /**
   * True when we cannot re-encode into the asset's stored format from a canvas.
   * A replace with a changed format needs `allow_format_change`, and moves the
   * direct `url` (only `stable_url` survives).
   */
  formatChanged: boolean;
}

/**
 * Which format to write the edited bytes as.
 *
 * We stay in the asset's own format whenever a browser canvas can encode it
 * (PNG / JPEG / WebP) so a replace keeps the direct URL. GIF, AVIF and ICO
 * cannot be produced by `toBlob`, so those become PNG — lossless, alpha-safe,
 * and flagged so the caller knows to allow the format change.
 */
export function outputFormatFor(extension: string): OutputFormat {
  const ext = (extension || '').toLowerCase().replace(/^\./, '');
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'jpe':
      return { mime: 'image/jpeg', extension: 'jpg', formatChanged: false };
    case 'webp':
      return { mime: 'image/webp', extension: 'webp', formatChanged: false };
    case 'png':
      return { mime: 'image/png', extension: 'png', formatChanged: false };
    default:
      // gif / avif / ico / svg / anything unexpected.
      return { mime: 'image/png', extension: 'png', formatChanged: true };
  }
}

/** `logo.png` + `png` → `logo-edited.png`; keeps the stem readable in the list. */
export function editedFilename(original: string, extension: string, suffix = 'edited'): string {
  const base = (original || 'image').replace(/\.[^./\\]+$/, '') || 'image';
  const safe =
    base
      // Anything that is not a plain filename character becomes a dash, and a
      // run of dots collapses to one — a stem of ".." would otherwise survive
      // a path-shaped upload name.
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/\.{2,}/g, '.')
      .slice(0, 80) || 'image';
  return `${safe}-${suffix}.${extension}`;
}

/** True when a crop/rotate/flip would be a no-op — used to disable "Save". */
export function isIdentityEdit(
  area: CropArea | null,
  transform: EditTransform,
  source: Size,
  outputWidth?: number | null,
): boolean {
  if (transform.flipHorizontal || transform.flipVertical) return false;
  if (normalizeRotation(transform.rotation) !== 0) return false;
  if (outputWidth && outputWidth !== source.width) return false;
  if (!area) return true;
  return (
    Math.abs(area.x) < 2 &&
    Math.abs(area.y) < 2 &&
    Math.abs(area.width - source.width) < 2 &&
    Math.abs(area.height - source.height) < 2
  );
}

// ─── Canvas work ──────────────────────────────────────────────────────────────

/** Load an image element. Same-origin only, so the canvas is never tainted. */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('That image could not be loaded for editing.')));
    image.src = src;
  });
}

export interface RenderOptions {
  /** Cap the exported width; the height follows the crop's ratio. */
  maxWidth?: number | null;
  /** Server ceiling from `GET /assets/config/`. */
  maxPixels?: number | null;
  mime?: string;
  quality?: number;
}

export interface RenderedImage {
  blob: Blob;
  width: number;
  height: number;
  /** The mime the browser actually produced — not always the one requested. */
  mime: string;
}

/**
 * Apply flip → rotate → crop → resize and return the encoded bytes.
 *
 * `area` must be the `croppedAreaPixels` reported by the cropper for the same
 * `transform.rotation`; see the module docstring for why.
 */
export async function renderEditedImage(
  image: HTMLImageElement,
  area: CropArea,
  transform: EditTransform,
  options: RenderOptions = {},
): Promise<RenderedImage> {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) {
    throw new Error('That image has no pixel dimensions and cannot be edited.');
  }

  const rotation = normalizeRotation(transform.rotation);
  const bbox = rotateSize(sourceWidth, sourceHeight, rotation);

  // Step 1: the rotated bounding box, in the cropper's coordinate space.
  const stage = document.createElement('canvas');
  stage.width = Math.round(bbox.width);
  stage.height = Math.round(bbox.height);
  const stageCtx = stage.getContext('2d');
  if (!stageCtx) throw new Error('This browser could not open a drawing canvas.');

  stageCtx.translate(stage.width / 2, stage.height / 2);
  stageCtx.rotate((rotation * Math.PI) / 180);
  stageCtx.scale(transform.flipHorizontal ? -1 : 1, transform.flipVertical ? -1 : 1);
  stageCtx.translate(-sourceWidth / 2, -sourceHeight / 2);
  stageCtx.drawImage(image, 0, 0, sourceWidth, sourceHeight);

  // Step 2: lift the crop out of the bounding box, resizing in the same draw.
  const safeArea = clampArea(area, { width: stage.width, height: stage.height });
  const output = fitOutputSize(
    { width: safeArea.width, height: safeArea.height },
    options.maxWidth,
    options.maxPixels,
  );

  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not open a drawing canvas.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const mime = options.mime || 'image/png';
  if (mime === 'image/jpeg') {
    // JPEG has no alpha channel: without this, transparent pixels encode as
    // black rather than as the white a logo is normally sitting on.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  ctx.drawImage(
    stage,
    safeArea.x,
    safeArea.y,
    safeArea.width,
    safeArea.height,
    0,
    0,
    output.width,
    output.height,
  );

  const blob = await canvasToBlob(canvas, mime, options.quality ?? 0.92);
  return { blob, width: output.width, height: output.height, mime: blob.type || mime };
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The edited image could not be encoded.'));
      },
      mime,
      quality,
    );
  });
}

/** Wrap rendered bytes as a `File` so it can go straight into a `FormData`. */
export function renderedToFile(rendered: RenderedImage, filename: string): File {
  return new File([rendered.blob], filename, { type: rendered.mime });
}
